
import React, { createContext, useContext, useEffect, useState } from 'react';
import { createUserProfile, getCurrentUser } from '../services/authService';
import { getUserProfile } from '../services/dataService';
import { UserProfile, UserRole } from '../types';
import { synczbase } from '../lib/synczbase';
import { getRoleForEmail } from '../utils/envAuth';

interface AuthContextType {
  user: any | null;
  profile: UserProfile | null;
  loading: boolean;
  role: UserRole | null;
  logout: () => Promise<void>;
  updateProfileState: (updatedProfile: UserProfile) => void; 
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  profile: null,
  loading: true,
  role: null,
  logout: async () => {},
  updateProfileState: () => {},
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<any | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const initAuth = async () => {
        try {
            const currentUser = await getCurrentUser();
            if (currentUser && isMounted) {
                setUser(currentUser);
                
                let dbProfile = await getUserProfile(currentUser.uid || currentUser.id);
                if (!dbProfile && currentUser.email) {
                    dbProfile = await getUserProfile(currentUser.email);
                }

                if (dbProfile) {
                    const envRole = getRoleForEmail(dbProfile.email || currentUser.email, dbProfile.role);
                    if (envRole === 'SUPER_ADMIN' || envRole === 'OWNER') {
                        dbProfile.role = envRole;
                    } else if (dbProfile.role === 'SUPER_ADMIN' || dbProfile.role === 'OWNER') {
                        // Preserve high-privilege DB role
                    } else if (currentUser.role && currentUser.role !== 'CLIENT') {
                        dbProfile.role = currentUser.role;
                    }
                    setProfile(dbProfile);
                } else {
                    const initialRole = getRoleForEmail(currentUser.email, currentUser.role || 'CLIENT');
                    const created = await createUserProfile(currentUser, initialRole);
                    setProfile(created || {
                        uid: currentUser.uid || currentUser.id,
                        email: currentUser.email || '',
                        role: initialRole,
                        displayName: currentUser.displayName || 'New User',
                        createdAt: Date.now()
                    } as UserProfile);
                }
                setLoading(false);
            } else {
                setLoading(false);
            }
        } catch (e) {
            setLoading(false);
        }
    };

    initAuth();
    
    const handleStorageChange = () => {
        if (!synczbase.getToken()) {
            setUser(null);
            setProfile(null);
        }
    };
    window.addEventListener('storage', handleStorageChange);

    return () => {
        isMounted = false;
        window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  const logout = async () => {
    synczbase.clearToken();
    setUser(null);
    setProfile(null);
    window.location.reload();
  };
  
  const updateProfileState = (updatedProfile: UserProfile) => {
      setProfile(updatedProfile);
  };

  return (
    <AuthContext.Provider value={{ user, profile, loading, role: profile?.role || user?.role || null, logout, updateProfileState }}>
      {children}
    </AuthContext.Provider>
  );
};
